<template>
  <div class="lottery" :class="{ 'is-android': isAndroid }">
    <div class="lottery__hero">
      <img class="lottery__title" src="@/assets/image/worldcupQuiz/lottery-title.png" alt="活动好礼">
      <img class="lottery__sub" src="@/assets/image/worldcupQuiz/lottery-subtitle.png" alt="抽中专享·100%中奖">
    </div>

    <div v-if="state === 'loading'" class="lottery__state">加载中...</div>
    <div v-else-if="state === 'error'" class="lottery__state">
      加载失败 <button @click="load">重试</button>
    </div>
    <template v-else>
      <wheel
        :prizes="prizes"
        :disabled="remaining <= 0 || prizeUnavailable"
        :spinning="drawing"
        :rotation="rotation"
      />
      <div v-if="prizeUnavailable" class="lottery__tip">奖品池配置未完成，暂不可抽奖</div>
      <div v-else class="lottery__intro">
        {{ introTitle }}<br>
        {{ introText }}
      </div>
      <button
        class="lottery__draw"
        :disabled="drawing || remaining <= 0 || prizeUnavailable"
        @click="startDraw"
      >
        {{ drawing ? '抽奖中' : '开始抽奖' }}
      </button>

      <div class="lottery__records">
        <div class="lottery__records-title">我的中奖记录</div>
        <div v-if="recordsLoading" class="lottery__record-empty">加载中...</div>
        <div v-else-if="!records.length" class="lottery__record-empty">暂无中奖记录</div>
        <div v-for="record in records" :key="recordKey(record)" class="lottery__record">
          <div class="lottery__record-icon">
            <img v-if="record.image" :src="record.image" alt="">
            <span v-else>{{ prizeTypeName(record.prize_type).slice(0, 1) }}</span>
          </div>
          <div class="lottery__record-main">
            <div class="lottery__record-name">{{ record.prize_name }}</div>
            <div class="lottery__record-time">{{ formatDateTime(record.create_time) }}</div>
          </div>
          <div class="lottery__record-status">{{ grantStatusName(record.grant_status) }}</div>
        </div>
      </div>
    </template>

    <prize-dialog
      :show="prizeDialog.show"
      :prize="prizeDialog.prize"
      @close="closePrize"
    />
  </div>
</template>

<script>
import { Toast } from 'vant'
import { createIdempotencyKey, draw, getLotteryCenter, getLotteryRecords } from '@/api/worldcupQuiz'
import PrizeDialog from '../components/PrizeDialog.vue'
import Wheel from '../components/Wheel.vue'
import { formatDateTime, grantStatusName, prizeTypeName, unwrapResponse, waitForNativeTouchEnd } from '../utils'

export default {
  name: 'WorldCupQuizLottery',
  components: { PrizeDialog, Wheel },
  data () {
    return {
      state: 'loading',
      recordsLoading: false,
      lotteryUnavailable: false,
      remaining: 0,
      prizes: [],
      records: [],
      drawing: false,
      rotation: 0,
      prizeDialog: {
        show: false,
        prize: null
      }
    }
  },
  computed: {
    isAndroid () {
      return /Android/i.test(window.navigator.userAgent || '')
    },
    prizeInvalid () {
      return this.prizes.length !== 8
    },
    prizeUnavailable () {
      return this.prizeInvalid || this.lotteryUnavailable
    },
    introTitle () {
      return this.remaining > 0 ? '猜中啦，快来抽好礼' : '暂无抽奖机会'
    },
    introText () {
      return this.remaining > 0
        ? `100% 中奖，点击下方开始抽奖（剩余 ${this.remaining} 次）`
        : '猜中后即可获得抽奖机会'
    }
  },
  created () {
    this.load()
  },
  methods: {
    formatDateTime,
    grantStatusName,
    prizeTypeName,
    async refreshCenter () {
      const data = unwrapResponse(await this.getLotteryCenterWithRetry())
      this.remaining = Number(data.remaining) || 0
      this.prizes = Array.isArray(data.prizes) ? data.prizes.slice().sort((a, b) => a.slot - b.slot) : []
      this.$emit('remaining-change', this.remaining)
      return data
    },
    async getLotteryCenterWithRetry () {
      const options = {
        skipToast: true,
        skipToastOnStatus0: true
      }
      const requestCenter = async () => {
        const resp = await getLotteryCenter(options)
        unwrapResponse(resp)
        return resp
      }
      try {
        return await requestCenter()
      } catch (error) {
        await waitForNativeTouchEnd()
        return requestCenter()
      }
    },
    async load () {
      this.state = 'loading'
      this.lotteryUnavailable = false
      try {
        await this.refreshCenter()
        this.state = 'success'
        this.loadRecords()
      } catch (error) {
        if (Number(error.code) === 631) this.$emit('blocked')
        else this.state = 'error'
      }
    },
    async loadRecords () {
      this.recordsLoading = true
      try {
        this.records = unwrapResponse(await getLotteryRecords(
          { page: 1, size: 20 },
          { skipToast: true, skipToastOnStatus0: true }
        )) || []
      } catch (error) {
        this.records = []
      } finally {
        this.recordsLoading = false
      }
    },
    async startDraw () {
      if (this.drawing || this.remaining <= 0 || this.prizeUnavailable) return
      this.drawing = true
      try {
        await waitForNativeTouchEnd()
        await this.refreshCenter()
        if (this.remaining <= 0) {
          Toast('无抽奖机会')
          this.drawing = false
          return
        }
        if (this.prizeUnavailable) {
          Toast('奖品池配置未完成，暂不可抽奖')
          this.drawing = false
          return
        }
        this.rotation += 720
        const result = unwrapResponse(await draw({ idempotency_key: createIdempotencyKey('draw') }))
        const slot = Math.min(Math.max(Number(result.prize_slot) || 1, 1), 8)
        const prize = this.mergePrizeFromConfig(result, slot)
        const target = (360 - (slot - 1) * 45) % 360
        const current = ((this.rotation % 360) + 360) % 360
        const delta = (target - current + 360) % 360
        this.rotation = this.rotation + 1080 + delta
        window.setTimeout(() => {
          this.remaining = Number(result.remaining) || 0
          this.$emit('remaining-change', this.remaining)
          this.prizeDialog = { show: true, prize }
          this.drawing = false
        }, 3200)
      } catch (error) {
        this.drawing = false
        if (Number(error.code) === 635) {
          this.remaining = 0
          this.$emit('remaining-change', 0)
          Toast('无抽奖机会')
        } else if (Number(error.code) === 636) {
          this.lotteryUnavailable = true
          Toast(error.message || '奖品池配置未完成，暂不可抽奖')
        } else if (Number(error.code) === 631) {
          Toast('暂无法参与本次活动')
          this.$emit('blocked')
        } else {
          Toast(error.message || '网络异常，请稍后重试')
        }
      }
    },
    mergePrizeFromConfig (result, slot) {
      const config = this.prizes.find(item => Number(item.slot) === Number(slot)) || {}
      return Object.assign({}, result, {
        prize_slot: slot,
        prize_name: config.name || result.prize_name,
        prize_type: result.prize_type || config.type,
        prize_amount: result.prize_amount === undefined ? config.grant_amount : result.prize_amount,
        image: config.image || result.image || ''
      })
    },
    async closePrize () {
      this.prizeDialog.show = false
      this.prizeDialog.prize = null
      await waitForNativeTouchEnd()
      this.loadRecords()
    },
    recordKey (record) {
      return `${record.create_time}-${record.prize_name}-${record.prize_type}`
    }
  }
}
</script>

<style scoped lang="scss">
.lottery {
  min-height: calc(100vh - 231px);
  padding: 0 0 128px;
  box-sizing: border-box;
  background: url('~@/assets/image/worldcupQuiz/lottery-top.png') center top/100% auto no-repeat;
  font-family: "PingFang SC", sans-serif;
}

.lottery__hero {
  padding-top: 1px;
  text-align: center;
}

.lottery__title {
  display: block;
  width: 169px;
  height: auto;
  margin-top: 18px;
  margin-left: auto;
  margin-right: auto;
}

.lottery__sub {
  display: block;
  width: 148px;
  height: auto;
  margin-top: 1px;
  margin-left: auto;
  margin-right: auto;
}

.lottery__state,
.lottery__record-empty {
  padding: 42px 0;
  color: rgba(255, 255, 255, .72);
  text-align: center;
  font-size: 14px;
}

.lottery__state button {
  margin-left: 8px;
  border: 0;
  color: #ffdf59;
  background: transparent;
  font-weight: 800;
}

.lottery__tip {
  margin-top: 8px;
  color: #ffd1c8;
  text-align: center;
  font-size: 13px;
}

.lottery__intro {
  width: 283px;
  margin: 8px auto 0;
  color: #fff6b6;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.lottery__draw {
  display: block;
  width: 335px;
  height: 56px;
  margin: 7px auto 0;
  border: 0;
  border-radius: 35px;
  color: #1d5a20;
  background: linear-gradient(90deg, #ffe648 0%, #ffb65a 100%);
  font-size: 18px;
  font-weight: 600;
  line-height: 56px;
}

.lottery__draw:disabled {
  opacity: .58;
}

.lottery__records {
  margin: 15px 21px 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
}

.lottery__records-title {
  color: #fff;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
}

.lottery__record {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 60px;
  margin-top: 14px;
  padding: 0 14px;
  box-sizing: border-box;
  border: 2px solid #ffe744;
  border-radius: 8px;
  background: rgba(10, 55, 18, .54);
}

.lottery__record:last-child {
  border-bottom: 2px solid #ffe744;
}

.lottery__record-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  color: #fff;
  border: 1px solid rgba(255, 219, 86, .42);
  background: #145720;
  line-height: 38px;
  text-align: center;
  font-weight: 900;
}

.lottery__record-icon img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.lottery__record-main {
  flex: 1;
  min-width: 0;
}

.lottery__record-name {
  color: #fff;
  font-size: 14px;
  font-weight: 800;
}

.lottery__record-time {
  margin-top: 3px;
  color: rgba(255, 255, 255, .58);
  font-size: 12px;
}

.lottery__record-status {
  color: #ffdf59;
  font-size: 13px;
  font-weight: 800;
}
</style>
